import { Button, ButtonIcon, css, Lead, PlusIcon, styled, TrashIcon } from 'canoe';
import { useFormikContext } from 'formik';
import get from 'lodash/get';
import React from 'react';

import { hasEditor, useHasPermission } from 'commons/auths';
import { DefinitionObjectSelector } from 'dashboard/pages/Segment/components/DefinitionFormFields';
import { Rule } from 'dashboard/pages/Segment/components/Rule';
import {
  DefinitionFormikValues,
  SegmentDefinition,
  SegmentSingleDefinition,
} from 'dashboard/pages/Segment/components/types';

import { addRuleToRuleGroup, isSingleRuleGroup, removeRuleFromRuleGroup } from './utils';

export const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

export const RuleSection = styled.div`
  ${({ theme }) => css`
    width: 100%;
    border: 1px solid ${theme.colors.GlitterGrayL10};
    border-radius: ${theme.borderRadius.small};
    padding: ${theme.spaces.s};
    margin: ${theme.spaces.xs} 0;
    display: flex;
    align-items: center;
    gap: ${theme.spaces.s};
  `}
`;

export const RuleIndex = styled(Lead)`
  ${({ theme }) => css`
    color: ${theme.colors.OnyxGrayL20};
    margin: 0;
  `}
`;

export const RuleContent = styled.div`
  flex-grow: 1;
`;

const DeleteRule = styled(ButtonIcon)`
  opacity: 0;
`;

const RuleRow = styled.div`
  ${({ theme }) => css`
    display: flex;
    align-items: center;
    flex-wrap: nowrap;
    gap: ${theme.spaces.s};

    &:not(:first-child) {
      margin-top: ${theme.spaces.s};
    }

    &:hover {
      ${DeleteRule} {
        opacity: 1;
      }
    }
  `}
`;

export const AddButton = styled.div`
  width: 90px;
`;

export interface RulesProps {
  definition: SegmentDefinition;
  onRemoveRule: (index: number) => void;
  readOnly?: boolean;
}

export const Rules = ({ definition, onRemoveRule, readOnly }: RulesProps) => {
  const { values, setValues } = useFormikContext<DefinitionFormikValues>();

  const canEdit = useHasPermission(hasEditor);
  const onAddEvent = (index: number) =>
    setValues({
      definition: {
        ...definition,
        groups: definition.groups?.map((ele, i) =>
          i === index ? addRuleToRuleGroup(definition.groups?.[index]) : ele,
        ),
      } as SegmentDefinition,
    });
  const onRemoveEvent = (groupIndex: number, childIndex: number) =>
    setValues({
      definition: {
        ...definition,
        groups: definition.groups?.map((ele, i) =>
          i === groupIndex
            ? removeRuleFromRuleGroup(definition.groups?.[groupIndex], childIndex)
            : ele,
        ),
      } as SegmentDefinition,
    });

  if (isSingleRuleGroup(definition)) {
    return (
      <RuleSection key="single-rule-section">
        <RuleIndex size="long">1</RuleIndex>
        <RuleContent>
          <Rule readOnly={readOnly} base="definition" />
        </RuleContent>
      </RuleSection>
    );
  }

  return (
    <>
      {definition.groups?.map((group, index) => (
        <Wrapper key={`${group}_${String(index)}`}>
          {index > 0 && (
            <DefinitionObjectSelector name="definition.object" readOnly={!canEdit || readOnly} />
          )}
          <RuleSection key={group.groupId}>
            <RuleIndex size="long">{index + 1}</RuleIndex>
            <RuleContent>
              {isSingleRuleGroup(get(values, `definition.groups[${index}]`)) ? (
                <RuleRow>
                  <Rule readOnly={readOnly || !canEdit} base={`definition.groups[${index}]`} />
                  {!readOnly && canEdit && (
                    <DeleteRule
                      onClick={(event: MouseEvent) => {
                        onRemoveRule(index);
                        event.preventDefault();
                      }}
                      icon={TrashIcon}
                      color="contrast"
                      size="small"
                      data-cy="segment-remove-rule-icon"
                    />
                  )}
                </RuleRow>
              ) : (
                (get(values, `definition.groups[${index}]`)
                  .groups as SegmentSingleDefinition[]).map((childGroup, childIndex) => (
                    <RuleRow key={`${childGroup}_${String(childIndex)}`}>
                      {childIndex > 0 && (
                      <DefinitionObjectSelector
                        nested
                        name={`definition.groups[${index}].object`}
                        readOnly={!canEdit || readOnly}
                      />
                    )}
                      <Rule
                        readOnly={readOnly || !canEdit}
                        base={`definition.groups[${index}].groups[${childIndex}]`}
                      />
                      {!readOnly && canEdit && (
                      <DeleteRule
                        onClick={(event: MouseEvent) => {
                          onRemoveEvent(index, childIndex);
                          event.preventDefault();
                        }}
                        icon={TrashIcon}
                        color="contrast"
                        size="small"
                        data-cy="segment-remove-rule-icon"
                      />
                    )}
                    </RuleRow>
                ))
              )}
              {!readOnly && canEdit && (
                <RuleRow>
                  <DefinitionObjectSelector
                    nested
                    name={`definition.groups[${index}].object`}
                    readOnly={!definition.groups?.[index].groups?.length}
                    isSingleRule={isSingleRuleGroup(get(values, `definition.groups[${index}]`))}
                  />

                  <AddButton>
                    <Button
                      variant="secondary"
                      onClick={() => onAddEvent(index)}
                      appearance="outline"
                      icon={PlusIcon}
                      fullWidth
                      data-cy="segment-add-nested-rule-button"
                    >
                      Add
                    </Button>
                  </AddButton>
                </RuleRow>
              )}
            </RuleContent>
          </RuleSection>
        </Wrapper>
      ))}
    </>
  );
};
